/**
 * Formulario de resultado post-llamada para el Call Sidebar.
 *
 * Aparece al colgar. Wizard interactivo:
 * 1. ¿Cómo fue? (6 opciones con emoji)
 * 2. Seguimiento (date chips)
 * 3. Resumen + Guardar
 */

import { useState } from 'react';
import { cn } from '@/lib/utils';
import { CheckCircleIcon, ArchiveBoxIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import { crmService } from '@/services/crmService';
import { useAuth } from '@/providers/AuthProvider';
import { useCRMUsers } from '@/hooks/useCRMUsers';
import type { CallResultCode, QuestionnaireData } from '@/types/callSidebar';
import {
  NextFollowUpSection,
  type NextFollowUpValue,
} from './NextFollowUpSection';

/** call_result values that REQUIRE scheduling a next follow-up. */
const NEXT_FOLLOW_UP_REQUIRED: Set<CallResultCode> = new Set([
  'answered_interested',
  'no_answer',
  'voicemail',
]);

// ─── Constants ───────────────────────────────────────────────────────────────

const RESULT_OPTIONS: {
  code: CallResultCode;
  label: string;
  emoji: string;
  color: string;
}[] = [
  {
    code: 'answered_interested',
    label: 'Interesado',
    emoji: '✅',
    color: 'bg-green-50 border-green-300 text-green-800 hover:bg-green-100',
  },
  {
    code: 'answered_not_interested',
    label: 'No interesado',
    emoji: '🙅',
    color: 'bg-yellow-50 border-yellow-300 text-yellow-800 hover:bg-yellow-100',
  },
  {
    code: 'no_answer',
    label: 'No contestó',
    emoji: '📵',
    color: 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100',
  },
  {
    code: 'voicemail',
    label: 'Buzón de voz',
    emoji: '📬',
    color: 'bg-purple-50 border-purple-300 text-purple-700 hover:bg-purple-100',
  },
  {
    code: 'wrong_number',
    label: 'Nº incorrecto',
    emoji: '❌',
    color: 'bg-red-50 border-red-300 text-red-800 hover:bg-red-100',
  },
  {
    code: 'busy',
    label: 'Comunicando',
    emoji: '📳',
    color: 'bg-orange-50 border-orange-300 text-orange-800 hover:bg-orange-100',
  },
];

function addDays(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

const FOLLOW_UP_OPTIONS = [
  { label: '1 día', days: 1 },
  { label: '3 días', days: 3 },
  { label: '1 sem', days: 7 },
  { label: '2 sem', days: 14 },
] as const;

// ─── Props ───────────────────────────────────────────────────────────────────

interface CallSidebarResultProps {
  data: QuestionnaireData;
  onChange: (patch: Partial<QuestionnaireData>) => void;
  onSave: () => void;
  saving: boolean;
  /** Número de caracteres de transcripción capturada */
  transcriptionChars: number;
  /** ID del contacto para archivar */
  contactId?: string;
  /** Valor actual del "próximo seguimiento" (null si incompleto). */
  nextFollowUp: NextFollowUpValue | null;
  /** Callback cuando cambia el próximo seguimiento. */
  onNextFollowUpChange: (value: NextFollowUpValue | null) => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function CallSidebarResult({
  data,
  onChange,
  onSave,
  saving,
  transcriptionChars,
  contactId,
  nextFollowUp,
  onNextFollowUpChange,
}: CallSidebarResultProps) {
  // Regla del dueño (2026-04-23): cuando el resultado implica que hay
  // que volver a contactar, la sección "próximo seguimiento" es
  // obligatoria — no se puede cerrar el form sin elegir quién + cuándo.
  const nextFollowUpRequired =
    !!data.call_result &&
    NEXT_FOLLOW_UP_REQUIRED.has(data.call_result as CallResultCode);
  const nextFollowUpOk = !nextFollowUpRequired || nextFollowUp !== null;
  // Si el usuario eligió una fecha en la sección "Seguimiento" simple,
  // exigimos también responsable — antes podía quedar la tarea huérfana.
  const simpleFollowUpResponsibleOk =
    !data.follow_up_date || !!data.follow_up_responsible_id;
  const canSave =
    !!data.call_result && nextFollowUpOk && simpleFollowUpResponsibleOk;
  const [archiving, setArchiving] = useState(false);

  // Responsables disponibles para el selector de la sección "Seguimiento"
  // simple (la sección NextFollowUpSection trae los suyos por su cuenta).
  const { user: currentUser } = useAuth();
  const { users: responsibleUsers } = useCRMUsers({
    onlyResponsibles: true,
    isActive: true,
  });

  return (
    <div className="px-4 py-3 space-y-4">
      {/* Header */}
      <p className="text-sm font-semibold text-gray-800 text-center">
        ¿Cómo fue la llamada?
      </p>

      {/* Result options */}
      <div className="grid grid-cols-2 gap-1.5">
        {RESULT_OPTIONS.map((opt) => (
          <button
            key={opt.code}
            type="button"
            onClick={() => onChange({ call_result: opt.code })}
            className={cn(
              'flex items-center gap-1.5 rounded-lg border px-2.5 py-2 text-xs font-medium transition-all text-left',
              opt.color,
              data.call_result === opt.code &&
                'ring-2 ring-offset-1 ring-blue-500 scale-[1.02]',
            )}
          >
            <span className="text-sm shrink-0">{opt.emoji}</span>
            <span className="leading-tight">{opt.label}</span>
          </button>
        ))}
      </div>

      {/* Follow-up — fecha simple. Se oculta cuando el call_result requiere
          NextFollowUpSection (que ya cubre fecha + responsable + tipo). Antes
          se mostraba siempre y permitía programar fecha sin responsable, lo
          que generaba tareas huérfanas (bug F-Llamada-1). */}
      {!nextFollowUpRequired && (
        <div>
          <p className="text-[11px] font-medium text-gray-500 mb-1.5">Seguimiento (opcional)</p>
          <div className="flex flex-wrap gap-1.5">
            {FOLLOW_UP_OPTIONS.map((opt) => {
              const date = addDays(opt.days);
              const selected = data.follow_up_date === date;
              return (
                <button
                  key={opt.label}
                  type="button"
                  onClick={() =>
                    onChange({
                      follow_up_date: selected ? '' : date,
                      // Si quita la fecha, también limpia el responsable.
                      follow_up_responsible_id: selected ? '' : data.follow_up_responsible_id,
                    })
                  }
                  className={cn(
                    'px-2.5 py-1 rounded-lg border text-xs font-medium transition-all',
                    selected
                      ? 'bg-blue-100 text-blue-800 border-blue-400 ring-1 ring-blue-400'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50',
                  )}
                >
                  {opt.label}
                </button>
              );
            })}
            <input
              type="date"
              value={
                data.follow_up_date &&
                !FOLLOW_UP_OPTIONS.some((o) => addDays(o.days) === data.follow_up_date)
                  ? data.follow_up_date
                  : ''
              }
              onChange={(e) => onChange({ follow_up_date: e.target.value })}
              min={new Date().toISOString().split('T')[0]}
              className="rounded-lg border border-gray-200 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>

          {/* Selector de responsable: aparece sólo cuando hay fecha. Si no
              se elige responsable, el botón Guardar queda deshabilitado. */}
          {data.follow_up_date && (
            <div className="mt-2">
              <label
                htmlFor="follow-up-responsible"
                className="text-[11px] font-medium text-gray-500 block mb-1"
              >
                Responsable del seguimiento <span className="text-red-500">*</span>
              </label>
              <select
                id="follow-up-responsible"
                value={data.follow_up_responsible_id || ''}
                onChange={(e) => onChange({ follow_up_responsible_id: e.target.value })}
                className="w-full text-sm border rounded px-2 py-1 bg-white dark:bg-gray-900"
              >
                <option value="" disabled>
                  Selecciona un responsable…
                </option>
                {(responsibleUsers ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name || u.email}
                    {u.id === currentUser?.id ? ' (yo)' : ''}
                  </option>
                ))}
              </select>
              {!data.follow_up_responsible_id && (
                <p className="mt-1 text-[11px] text-amber-600">
                  Elige a quién se le asignará la tarea de seguimiento del {data.follow_up_date}.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Next follow-up (Sprint 6) — obligatorio para algunos resultados.
          Ya incluye su propio selector de tipo + responsable + cuándo. */}
      {data.call_result && (
        <NextFollowUpSection
          callResult={data.call_result}
          onChange={onNextFollowUpChange}
          required={nextFollowUpRequired}
        />
      )}

      {/* Transcription indicator */}
      {transcriptionChars > 0 && (
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200">
          <span className="text-xs text-emerald-600">📝</span>
          <span className="text-[11px] text-emerald-700 font-medium">
            Transcripción ({transcriptionChars} chars) se guardará automáticamente
          </span>
        </div>
      )}

      {/* Save button */}
      <button
        type="button"
        onClick={onSave}
        disabled={!canSave || saving}
        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-green-600 text-white text-sm font-bold hover:bg-green-700 active:bg-green-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        {saving ? (
          <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
        ) : (
          <CheckCircleIcon className="h-4 w-4" />
        )}
        {saving ? 'Guardando...' : 'Guardar y cerrar'}
      </button>

      {/* Archive contact button */}
      {contactId && (
        <button
          type="button"
          onClick={async () => {
            if (!window.confirm('¿Archivar este contacto? No volverá a aparecer en las colas de llamada.')) return;
            setArchiving(true);
            try {
              const reason = data.call_result === 'wrong_number'
                ? 'Número incorrecto'
                : data.call_result === 'answered_not_interested'
                  ? 'No interesado'
                  : 'Archivado desde panel de llamada';
              await crmService.archiveContact(contactId, { reason });
              toast.success('Contacto archivado — no se le volverá a llamar');
            } catch {
              toast.error('Error al archivar el contacto');
            } finally {
              setArchiving(false);
            }
          }}
          disabled={archiving}
          className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-amber-400 text-amber-700 text-xs font-medium hover:bg-amber-50 active:bg-amber-100 disabled:opacity-40 transition-colors"
        >
          <ArchiveBoxIcon className="h-3.5 w-3.5" />
          {archiving ? 'Archivando...' : 'Archivar contacto (no volver a llamar)'}
        </button>
      )}
    </div>
  );
}
